import React from 'react'
import type { ReactNode } from 'react'
import type { UseFormRegister, FieldErrors } from 'react-hook-form'
import { Input, Select, Textarea } from '../ui'
import TermChange from './TermChange'
import type { TermFieldConfig } from './types'

interface TermRowProps {
  field: TermFieldConfig
  deal: Record<string, unknown>
  viewOnly: boolean
  isNegotiating: boolean
  register?: UseFormRegister<Record<string, unknown>>
  errors?: FieldErrors
  formatChange: (change: number, unit?: string) => ReactNode
}

const TermRow = ({ field, deal, viewOnly, isNegotiating, register, errors, formatChange }: TermRowProps) => {
  const {
    key,
    label,
    diffField,
    changeField,
    valueField,
    previousField,
    formField,
    unit,
    showBorder = true,
    showPercentDiff = true,
    formatValue,
    formatPrevious,
    input,
  } = field

  const diff = deal.diff as { changes: Record<string, unknown>; previous: Record<string, unknown> } | undefined
  const hasChange = diff?.changes?.[diffField] !== undefined
  const actualValueField = valueField || key
  const actualFormField = formField || key
  const actualChangeField = changeField || diffField
  const actualPreviousField = previousField || diffField
  const isTextarea = input?.type === 'textarea'

  const borderClass = showBorder ? 'sm:border-b border-gray-400 ' : ''
  const labelBorderClass = showBorder ? 'sm:border-b border-gray-400 ' : ''
  const currentBorderClass = showBorder ? 'border-b border-gray-400 ' : ''
  const heightClass = isTextarea ? 'items-start h-fit' : 'sm:items-center sm:h-16'

  const previousValue = diff?.previous?.[actualPreviousField]
  const currentValue = deal[actualValueField]

  const renderPreviousValue = () => {
    if (previousValue === undefined) return null
    if (input?.type === 'select' || input?.type === 'textarea' || typeof previousValue === 'string') {
      return <span>{previousValue as string}</span>
    }
    const formatted = formatPrevious ? formatPrevious(previousValue) : previousValue
    return (
      <div className="space-x-1">
        <span>{formatted as string | number}</span>
        {unit && <span className="text-gray-400 text-xs">{unit}</span>}
      </div>
    )
  }

  const renderCurrentValue = () => {
    if (input?.type === 'select' || (!input && typeof currentValue === 'string' && !unit)) {
      return <span className={`font-semibold ${isNegotiating ? 'hidden' : ''}`}>{currentValue as string}</span>
    }
    const formatted = formatValue ? formatValue(currentValue) : currentValue
    return (
      <div className={`space-x-1 ${isNegotiating ? 'hidden' : ''}`}>
        <span className="font-semibold">{formatted as string | number}</span>
        {unit && <span className="text-gray-400 text-xs">{unit}</span>}
      </div>
    )
  }

  const renderInput = () => {
    if (!input || !register) return null
    if (input.type === 'number') {
      return (
        <Input
          type="number"
          step={input.step || '0.01'}
          name={actualFormField}
          id={actualFormField}
          register={register(actualFormField, input.validation || { required: 'Required' })}
          errors={errors}
          postfix={unit}
          className="pr-20"
        />
      )
    }
    if (input.type === 'select' && input.options) {
      return (
        <Select
          id={actualFormField}
          name={actualFormField}
          options={input.options}
          register={register(actualFormField, input.validation || { required: 'Required' })}
          errors={errors}
        />
      )
    }
    if (input.type === 'textarea') {
      return (
        <Textarea
          id={actualFormField}
          name={actualFormField}
          rows={input.rows || 15}
          register={register(actualFormField, input.validation)}
          errors={errors}
        />
      )
    }
    return null
  }

  return (
    <>
      {/* Label column */}
      <div
        className={`${labelBorderClass}not-empty-pb-6 sm:flex ${heightClass} pt-6 sm:pt-0 text-center sm:text-left sm:text-sm font-semibold sm:font-medium text-gray-700`}>
        <label htmlFor={actualFormField} className={isNegotiating ? 'sm:self-start' : ''}>
          {label}
        </label>
      </div>

      {/* Previous value column */}
      <div className={`${borderClass}not-empty-pb-6 sm:flex ${heightClass} sm:px-6`}>
        {!viewOnly && hasChange && (
          <div className="flex justify-between items-center">
            <div className="inline sm:hidden">Previous offer</div>
            {renderPreviousValue()}
          </div>
        )}
      </div>

      {/* Changes column */}
      <div className={`${borderClass}not-empty-pb-6 sm:flex ${heightClass} sm:px-6`}>
        {!viewOnly && hasChange && (
          <div className="flex justify-between items-center">
            <div className="inline sm:hidden">Changes</div>
            <TermChange
              changes={diff?.changes || {}}
              diffField={actualChangeField}
              unit={unit}
              showPercentDiff={showPercentDiff}
              formatChange={formatChange}
            />
          </div>
        )}
      </div>

      {/* Current value / Input column */}
      <div className={`${currentBorderClass}not-empty-pb-6 sm:flex ${heightClass}`}>
        <div className="flex justify-between items-center w-full">
          <div className="inline sm:hidden flex-1">Current offer</div>
          <div className="flex-1 text-right sm:text-left w-full">
            {renderCurrentValue()}
            {!viewOnly && input && (
              <div className={!isNegotiating ? 'hidden' : ''}>
                {renderInput()}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default TermRow
